import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, TrendingUp, Download, Printer } from 'lucide-react';
import { DataStore, Project } from '@/lib/data-store';
import { ProjectForm } from '@/components/ProjectForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { generateProjectCode, getNextProjectNumber } from '@/lib/project-utils';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const NilaiKosProjek = () => {
  const data = DataStore.getData();
  const [projects, setProjects] = useState<Project[]>(data.projects);
  const [customers] = useState(data.customers);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: Project) => {
    const newProjects = editingId
      ? projects.map(p => p.id === editingId ? { ...data, id: editingId } : p)
      : [...projects, data];

    setProjects(newProjects);
    DataStore.saveData({ ...DataStore.getData(), projects: newProjects });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = projects.filter(p => p.id !== id);
    setProjects(filtered);
    DataStore.saveData({ ...DataStore.getData(), projects: filtered });
  };

  const filteredProjects = useMemo(() => projects.filter(p => {
    const matchesSearch = p.namaProjek.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.pelanggan.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !selectedProject || p.namaProjek === selectedProject;
    return matchesSearch && matchesProject;
  }), [projects, searchTerm, selectedProject]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalNilai = projects.reduce((sum, p) => sum + p.nilaiProjek, 0);
    const totalKos = projects.reduce((sum, p) => sum + p.kosProjek, 0);
    return {
      totalNilai,
      totalKos,
      totalKeuntungan: totalNilai - totalKos
    };
  }, [projects]);

  // Prepare chart data
  const chartData = useMemo(() => filteredProjects.map(p => ({
    nama: p.namaProjek.substring(0, 15) + (p.namaProjek.length > 15 ? '...' : ''),
    Nilai: p.nilaiProjek,
    Kos: p.kosProjek,
    Keuntungan: p.nilaiProjek - p.kosProjek,
  })), [filteredProjects]);

  const nextProjectCode = generateProjectCode(getNextProjectNumber(projects));

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="cost" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">5. Nilai & Kos Projek</h1>
          <p className="text-slate-600 mt-1">Analisis nilai projek dan kos operasional</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {projects.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredProjects, [
                    { label: 'Kod Projek', key: 'kodProjek' },
                    { label: 'Nama Projek', key: 'namaProjek' },
                    { label: 'Pelanggan', key: 'pelanggan' },
                    { label: 'Nilai (RM)', key: 'nilaiProjek', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Kos (RM)', key: 'kosProjek', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Status', key: 'status' }
                  ]);
                  printWithLetterhead('NILAI & KOS PROJEK', tableHTML, 'ANALISIS PROJEK / PROJECT ANALYSIS');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredProjects, 'Nilai Kos Projek')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Projek Baru</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Jumlah Nilai Projek</p>
              <p className="text-2xl font-bold text-blue-600">RM {totalNilai.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{projects.length} projek</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Jumlah Kos Projek</p>
              <p className="text-2xl font-bold text-orange-600">RM {totalKos.toFixed(2)}</p>
              <p className="text-xs text-slate-500">Keseluruhan kos</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Keuntungan Kasar</p>
              <p className={`text-2xl font-bold ${totalKeuntungan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                RM {totalKeuntungan.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500">Untung/Rugi</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Perbandingan Nilai & Kos Projek</CardTitle>
            <CardDescription>Analisis margin keuntungan per projek</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nama" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip formatter={(value) => `RM ${value.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="Nilai" fill="#3b82f6" />
                  <Bar dataKey="Kos" fill="#f97316" />
                  <Bar dataKey="Keuntungan" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-slate-400" size={20} />
            <Input 
              placeholder="Cari projek..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Projek</CardTitle>
          <CardDescription>Jumlah {filteredProjects.length} projek</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredProjects.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data projek. Sila tambahkan projek baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="projects-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Kod Projek</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Nama Projek</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Pelanggan</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Nilai (RM)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Kos (RM)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Keuntungan (RM)</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => {
                    const keuntungan = project.nilaiProjek - project.kosProjek;
                    return (
                      <tr key={project.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium text-slate-900">{project.kodProjek}</td>
                        <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline cursor-pointer" onClick={() => setSelectedProject(project.namaProjek)}>{project.namaProjek}</td>
                        <td className="py-3 px-4 text-slate-600 font-medium text-blue-600 hover:underline cursor-pointer" onClick={() => setSelectedProject(project.namaProjek)}>{project.pelanggan}</td>
                        <td className="py-3 px-4 text-right font-medium text-blue-600">RM {project.nilaiProjek.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-orange-600">RM {project.kosProjek.toFixed(2)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${keuntungan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          RM {keuntungan.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            project.status === 'Selesai' ? 'bg-green-100 text-green-700' :
                            project.status === 'Dalam Proses' ? 'bg-blue-100 text-blue-700' :
                            project.status === 'Tender' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 flex gap-2">
                          <button onClick={() => handleEdit(project.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                            <Edit2 size={14} /> Sunting
                          </button>
                          <button onClick={() => handleDelete(project.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                            <Trash2 size={14} /> Padam
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Form Modal */}
      {showForm && (
        <ProjectForm
          project={editingId ? projects.find(p => p.id === editingId) : undefined}
          kodProjek={editingId ? (projects.find(p => p.id === editingId)?.kodProjek || nextProjectCode) : nextProjectCode}
          customers={customers}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default NilaiKosProjek;